import React from 'react'
import { UIRadioGroupBase as RadioGroup, UIRadioGroupItemBase as RadioGroupItem } from './ui-radio-group'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIRadioGroupColorOption {
  value: string
  label: string
  color?: string
  id?: string
  disabled?: boolean
  /**
   * 미리 정의된 색상 사용 여부
   */
  preset?: 'red' | 'orange' | 'amber' | 'yellow' | 'lime' | 'green' | 'emerald' | 'teal' | 'cyan' | 'sky' | 'blue' | 'indigo' | 'violet' | 'purple' | 'fuchsia' | 'pink' | 'rose'
}

export interface UIRadioGroupColorProps extends React.ComponentProps<typeof RadioGroup> {
  options: UIRadioGroupColorOption[]
  label?: string
  description?: string
  /**
   * 색상 원 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 색상 표시 스타일
   */
  variant?: 'circle' | 'square' | 'rounded'
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 필수 여부
   */
  required?: boolean
}

const sizeVariants = {
  sm: {
    color: 'w-6 h-6',
    container: 'gap-2',
    text: 'text-xs leading-4',
    label: 'text-sm leading-4',
    description: 'text-xs leading-4',
    error: 'text-xs leading-4'
  },
  default: {
    color: 'w-8 h-8',
    container: 'gap-3',
    text: 'text-sm leading-4',
    label: 'text-base leading-5',
    description: 'text-sm leading-4',
    error: 'text-sm leading-4'
  },
  lg: {
    color: 'w-10 h-10',
    container: 'gap-4',
    text: 'text-base leading-5',
    label: 'text-lg leading-6',
    description: 'text-base leading-5',
    error: 'text-sm leading-4'
  }
}

const presetColors = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  yellow: 'bg-yellow-500',
  lime: 'bg-lime-500',
  green: 'bg-green-500',
  emerald: 'bg-emerald-500',
  teal: 'bg-teal-500',
  cyan: 'bg-cyan-500',
  sky: 'bg-sky-500',
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  purple: 'bg-purple-500',
  fuchsia: 'bg-fuchsia-500',
  pink: 'bg-pink-500',
  rose: 'bg-rose-500'
}

/**
 * 색상 선택 라디오 그룹 컴포넌트
 * 색상 팔레트 형태로 선택 옵션을 표시하며, 사용자 정의 색상과 프리셋 색상을 지원합니다.
 */
export const UIRadioGroupColor = React.forwardRef<
  React.ComponentRef<typeof RadioGroup>,
  UIRadioGroupColorProps
>(({
  className,
  options,
  label,
  description,
  size = 'default',
  variant = 'circle',
  error,
  required = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}, ref) => {
  const sizeClass = sizeVariants[size]
  const [internalValue, setInternalValue] = React.useState(value || defaultValue || '')

  // Update internal value when controlled value changes
  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value || '')
    }
  }, [value])

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  const getVariantClasses = () => {
    switch (variant) {
      case 'square':
        return 'rounded-none'
      case 'rounded':
        return 'rounded-md'
      case 'circle':
      default:
        return 'rounded-full'
    }
  }

  return (
    <UIBox className="space-y-3">
      {label && (
        <UILabel className={cn(
          "font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          sizeClass.label,
          required && "after:content-['*'] after:ml-0.5 after:text-destructive",
          error && "text-destructive"
        )}>
          {label}
        </UILabel>
      )}
      {description && (
        <UITypography
          variant="p"
          color="muted"
          className={sizeClass.description}
        >
          {description}
        </UITypography>
      )}

      <RadioGroup
        ref={ref}
        className={cn("flex flex-wrap", sizeClass.container, className)}
        value={internalValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {options.map((option, index) => {
          const itemId = option.id || `${props.name || 'color-radio'}-${option.value}-${index}`
          const colorStyle = option.preset
            ? presetColors[option.preset]
            : undefined

          return (
            <UIBox key={option.value} className="flex flex-col items-center gap-2">
              <UIBox className="relative">
                <RadioGroupItem
                  value={option.value}
                  id={itemId}
                  disabled={option.disabled}
                  className="sr-only"
                />
                <UILabel
                  htmlFor={itemId}
                  className={cn(
                    "relative flex cursor-pointer items-center justify-center border-2 border-transparent transition-[transform,translate,scale,box-shadow] hover:scale-110",
                    sizeClass.color,
                    getVariantClasses(),
                    option.disabled && "opacity-50 cursor-not-allowed",
                    error && "ring-2 ring-destructive ring-offset-2",
                    // 체크된 상태
                    "peer-data-[state=checked]:ring-2 peer-data-[state=checked]:ring-primary peer-data-[state=checked]:ring-offset-2 peer-data-[state=checked]:scale-110"
                  )}
                  style={{
                    backgroundColor: option.preset ? undefined : option.color
                  }}
                >
                  {/* 프리셋 색상 사용 시 */}
                  {option.preset && (
                    <UIBox className={cn(
                      "absolute inset-0",
                      colorStyle,
                      getVariantClasses()
                    )} />
                  )}

                  {/* 체크 표시 */}
                  {internalValue === option.value && (
                    <Check className={cn(
                      "relative z-10 text-white drop-shadow-sm",
                      size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'
                    )} />
                  )}

                  {/* 그라데이션 오버레이 (더 나은 가시성을 위해) */}
                  <UIBox className={cn(
                    "absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity peer-data-[state=checked]:opacity-100",
                    getVariantClasses()
                  )} />
                </UILabel>
              </UIBox>

              {/* 색상 라벨 */}
              <UILabel
                htmlFor={itemId}
                className={cn(
                  "cursor-pointer text-center font-medium",
                  sizeClass.text,
                  option.disabled && "opacity-50 cursor-not-allowed",
                  error && "text-destructive"
                )}
              >
                {option.label}
              </UILabel>
            </UIBox>
          )
        })}
      </RadioGroup>

      {error && (
        <UITypography
          variant="p"
          color="destructive"
          className={cn("animate-in slide-in-from-top-1 duration-200", sizeClass.error)}
          role="alert"
        >
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

UIRadioGroupColor.displayName = "UIRadioGroupColor"